/**
 * Copyright (c) Microsoft. All rights reserved.
 */
import { IComponentStyles } from '@fluentui/foundation-legacy';
import { DefaultButton } from '@fluentui/react/lib/Button';
import { Icon } from '@fluentui/react/lib/Icon';
import { IPanelProps, Panel } from '@fluentui/react/lib/Panel';
import { Spinner } from '@fluentui/react/lib/Spinner';
import { IStackProps, IStackSlots, Stack } from '@fluentui/react/lib/Stack';
import { classNamesFunction } from '@fluentui/react/lib/Utilities';
import { observer } from 'mobx-react';
import React from 'react';
import { defineMessages } from 'react-intl';
import { ExternalLink } from 'src/core/components/ExternalLink';
import { LuisText } from 'src/core/components/LuisText';
import { useLocalization } from 'src/core/hooks/useLocalization';
import { AzureStorageAccountStore } from 'src/core/stores/AzureStorageAccountStore';
import { TranslatorLanguage } from 'src/languageStudio/core/hooks/useTranslatorCapabilities';
import { getBlobStoreUrl } from 'src/languageStudio/core/utils/demosUtils';
import { TranslationJobStore } from 'src/translation/stores/TranslationJobStore';

const messages = defineMessages({
    panelTitle: {
        id: 'translation.translationJobDetailsPanel.panelTitle',
        defaultMessage: 'Translation job details'
    },
    panelClose: {
        id: 'translation.translationJobDetailsPanel.panelClose',
        defaultMessage: 'Close'
    },
    jobId: {
        id: 'translation.translationJobDetailsPanel.jobId',
        defaultMessage: 'Job ID'
    },
    sourceLanguage: {
        id: 'translation.translationJobDetailsPanel.sourceLanguage',
        defaultMessage: 'Source language'
    },
    targetLanguages: {
        id: 'translation.translationJobDetailsPanel.targetLanguages',
        defaultMessage: 'Target languages'
    },
    targetLanguage: {
        id: 'translation.translationJobDetailsPanel.targetLanguage',
        defaultMessage: 'Target language'
    },
    jobStatus: {
        id: 'translation.translationJobDetailsPanel.jobStatus',
        defaultMessage: 'Job status'
    },
    submitted: {
        id: 'translation.translationJobDetailsPanel.submitted',
        defaultMessage: 'Submitted'
    },
    totalTime: {
        id: 'translation.translationJobDetailsPanel.totalTime',
        defaultMessage: 'Total time'
    },
    destination: {
        id: 'translation.translationJobDetailsPanel.destination',
        defaultMessage: 'Documents output to'
    },
    source: {
        id: 'translation.translationJobDetailsPanel.source',
        defaultMessage: 'Documents source from'
    },
    errorDetails: {
        id: 'translation.translationJobDetailsPanel.errorDetails',
        defaultMessage: 'Show error details'
    },
    blobStorage: {
        id: 'translation.translationJobDetailsPanel.blobStorage',
        defaultMessage: 'Blob storage'
    },
    destinationFolder: {
        id: 'translation.translationJobDetailsPanel.destinationFolder',
        defaultMessage: 'Destination folder'
    }
});

export type TranslationJobDetailsPanelProps = IPanelProps & {
    isOpen: boolean;
    onDismiss: () => void;
    translationJob?: TranslationJobStore;
    supportedLanguages: TranslatorLanguage[];
    storageAccounts: AzureStorageAccountStore[];
    isStorageAccountsLoading: boolean;
};

const panelChildrenContentStyles = classNamesFunction<IStackProps, IComponentStyles<IStackSlots>>()({
    root: { marginTop: '40px' }
});

const formatMillisecondsToTimeString = (timeInMilliseconds: number) => {
    let h;
    let m;
    let s;
    h = Math.floor(timeInMilliseconds / 1000 / 60 / 60);
    m = Math.floor((timeInMilliseconds / 1000 / 60 / 60 - h) * 60);
    s = Math.floor(((timeInMilliseconds / 1000 / 60 / 60 - h) * 60 - m) * 60);

    s < 10 ? (s = `0${s}`) : (s = `${s}`);
    m < 10 ? (m = `0${m}`) : (m = `${m}`);
    h < 10 ? (h = `0${h}`) : (h = `${h}`);

    return `${h}:${m}:${s}`;
};

export const TranslationJobDetailsPanel = observer((props: TranslationJobDetailsPanelProps) => {
    const {
        isOpen = false,
        onDismiss,
        translationJob = new TranslationJobStore('', {}),
        supportedLanguages,
        storageAccounts,
        isStorageAccountsLoading
    } = props;

    const { localize } = useLocalization();
    const [areErrorDetailsVisible, setAreErrorDetailsVisible] = React.useState(false);

    const createStorageAccountUrl = (storageAccountName: string, containerName: string) => {
        const storageAccStore = storageAccounts.find(s => s.name === storageAccountName);

        return getBlobStoreUrl(storageAccStore.id, containerName);
    };

    const getPanelStackElement = (
        title: ReactIntl.FormattedMessage.MessageDescriptor,
        details: string | number,
        extraDetails?: string | number
    ) => (
        <Stack tokens={{ childrenGap: 10 }}>
            <LuisText fontWeight="bold">{localize(title)}</LuisText>
            {extraDetails ? (
                <Stack tokens={{ childrenGap: 5 }}>
                    <LuisText>{details}</LuisText>
                    <LuisText>{extraDetails}</LuisText>
                </Stack>
            ) : (
                <LuisText>{details}</LuisText>
            )}
        </Stack>
    );

    const getBlobStoreLinkStackElement = (
        storageAccountName: string,
        containerName: string,
        isDestination: boolean,
        folderName?: string
    ) => (
        <Stack tokens={{ childrenGap: 10 }}>
            {isDestination && <LuisText fontWeight="bold">{localize(messages.destination)}</LuisText>}
            {!isDestination && <LuisText fontWeight="bold">{localize(messages.source)}</LuisText>}

            {!isStorageAccountsLoading ? (
                <Stack horizontal tokens={{ childrenGap: 5 }}>
                    <LuisText>{localize(messages.blobStorage)}</LuisText>

                    <ExternalLink href={createStorageAccountUrl(storageAccountName, containerName)}>
                        {`${storageAccountName}/${containerName}${folderName ? `/${folderName}` : ''}`}
                    </ExternalLink>
                </Stack>
            ) : (
                <Spinner />
            )}
        </Stack>
    );

    const onRenderFooterContent = React.useCallback(
        () => (
            <div>
                <DefaultButton onClick={onDismiss} text={localize(messages.panelClose)} />
            </div>
        ),
        [onDismiss]
    );

    const getTargetLanguages = () =>
        translationJob.documentStoreHouse.items
            .map(job => supportedLanguages.find(l => l.code === job.targetLanguageCode)?.name)
            .filter((lang, i, arr) => arr.indexOf(lang) === i);

    return (
        <Panel
            isLightDismiss
            isOpen={isOpen}
            onDismiss={onDismiss}
            headerText={localize(messages.panelTitle)}
            closeButtonAriaLabel={localize(messages.panelClose)}
            onRenderFooterContent={onRenderFooterContent}
            isFooterAtBottom>
            <Stack styles={panelChildrenContentStyles} tokens={{ childrenGap: 20 }}>
                {getPanelStackElement(messages.jobId, translationJob.id)}

                {/* Remove the source language column until the APIs return them */}
                {/* https://msazure.visualstudio.com/Cognitive%20Services/_workitems/edit/15674871 */}
                {/* {getPanelStackElement(
                    messages.sourceLanguage,
                    translationJob.documentStoreHouse.items.length ? translationJob.documentStoreHouse.items[0]?.sourceLanguageCode : 'N/A'
                )} */}

                {getPanelStackElement(
                    getTargetLanguages().length > 1 ? messages.targetLanguages : messages.targetLanguage,
                    translationJob.documentStoreHouse.items.length ? getTargetLanguages().join(', ') : 'N/A'
                )}

                {getPanelStackElement(messages.submitted, translationJob.createdDateTimeUtc?.toLocaleString())}

                {getPanelStackElement(
                    messages.totalTime,
                    formatMillisecondsToTimeString(
                        Math.abs(translationJob.lastActionDateTimeUtc.getTime() - translationJob.createdDateTimeUtc.getTime())
                    )
                )}
                {getPanelStackElement(messages.jobStatus, `Translation ${translationJob.status}`)}

                {translationJob.documentStoreHouse.items.length &&
                    getBlobStoreLinkStackElement(
                        translationJob.documentStoreHouse.items[0].sourceStorageAccountName,
                        translationJob.documentStoreHouse.items[0].sourceContainerName,
                        false,
                        translationJob.documentStoreHouse.items[0].sourceFolderName
                    )}

                {translationJob.documentStoreHouse.items.length &&
                    translationJob.documentStoreHouse.items[0].status === 'Succeeded' &&
                    getBlobStoreLinkStackElement(
                        translationJob.documentStoreHouse.items[0].destinationStorageAccountName,
                        translationJob.documentStoreHouse.items[0].destinationContainerName,
                        true,
                        translationJob.documentStoreHouse.items[0].destinationFolderName
                    )}
                {translationJob.documentStoreHouse.items.length &&
                    translationJob.documentStoreHouse.items[0].status === 'Succeeded' &&
                    translationJob.documentStoreHouse.items[0].destinationFolderName &&
                    getPanelStackElement(messages.destinationFolder, translationJob.documentStoreHouse.items[0].destinationFolderName)}

                {translationJob.error && (
                    <Stack>
                        <Stack horizontal verticalAlign="center" tokens={{ childrenGap: 5 }}>
                            <ExternalLink onClick={() => setAreErrorDetailsVisible(!areErrorDetailsVisible)}>
                                {localize(messages.errorDetails)}
                            </ExternalLink>
                            <Icon iconName={areErrorDetailsVisible ? 'ChevronUp' : 'ChevronDown'} />
                        </Stack>

                        {areErrorDetailsVisible && translationJob.error && <li key={translationJob.error}>{translationJob.error}</li>}
                    </Stack>
                )}
            </Stack>
        </Panel>
    );
});
